import { createShapeNormalizer } from '@change-corgi/fcm/normalizers';

import { defineFcm } from '../shared/factory';

type Tag = {
	min: number;
	max: number;
	label: string;
	color: string;
	labelColor: string;
	textColor: string;
};

type Tags = Tag[];

const tagNormalizer = (value: unknown) => {
	const tag = value as Tag;
	const pattern = /^#[a-f0-9]+$/i;
	if (!pattern.test(tag.labelColor) || !pattern.test(tag.textColor)) {
		throw new Error('Expecting color to be a hex code');
	}
	return tag;
};

const tagsListNormalizer = (value: unknown) => {
	const tags = value as Tags;

	if (tags.length === 0) {
		throw new Error('Expecting tags to be a non-empty array.');
	}

	const ascendingOrderTags = tags.sort((a, b) => a.min - b.min);

	const firstTag = ascendingOrderTags[0];
	const lastTag = ascendingOrderTags[ascendingOrderTags.length - 1];
	if (!firstTag || !lastTag || firstTag.min !== 0 || lastTag.max !== 100) {
		throw new Error('Tag ranges must cover values from 0 to 100.');
	}

	ascendingOrderTags.forEach((tag, index) => {
		if (index + 1 >= ascendingOrderTags.length) return;

		const nextTag = ascendingOrderTags[index + 1];
		if (!nextTag) return;
		if (tag.max + 1 !== nextTag.min) {
			if (tag.max + 1 < nextTag.min) {
				throw new Error('Tag ranges must cover values from 0 to 100.');
			}

			if (tag.max + 1 > nextTag.min) {
				throw new Error('Tag ranges cannot overlap.');
			}
		}
	});
	return tags;
};

const levelNormalizer = (value: unknown) => {
	const levels = value as string[];
	const allowedValues = ['state', 'federal', 'city', 'regional', 'local', 'county'];

	// each value has to be in the list of allowed values
	levels.forEach((val) => {
		if (!allowedValues.includes(val)) {
			throw new Error(`Level can only include: ${allowedValues.join(', ')}`);
		}
	});

	return levels;
};

export const FCM_DM_PROFILE_RESPONSIVENESS_SCORE_CONFIG = defineFcm('dm_profile_responsiveness_score_config')
	.asJson(
		createShapeNormalizer({
			type: 'object',
			shape: {
				contact: {
					type: 'object',
					shape: {
						email: { type: 'string' },
						phone: { type: 'string' },
					},
				},
				redshiftQueryParams: {
					optional: true,
					type: 'object',
					shape: {
						minPetitionDate: { type: 'string' },
						minRelevantPetitions: { type: 'number' },
						minResponsivenessScore: { type: 'number' },
						maxResponsivenessScore: { type: 'number' },
						levels: { type: 'array', shape: { type: 'string' }, normalizer: levelNormalizer },
					},
				},
				tags: {
					type: 'array',
					optional: true,
					shape: {
						type: 'object',
						shape: {
							min: { type: 'number' },
							max: { type: 'number' },
							label: { type: 'string' },
							color: { type: 'string' },
							labelColor: { type: 'string', optional: true },
							textColor: { type: 'string', optional: true },
						},
						normalizer: tagNormalizer,
					},
					normalizer: tagsListNormalizer,
				},
			},
		}),
	)
	.withDefault({
		contact: {
			email: 'test@change.org',
			phone: 'dummy-phone',
		},
		tags: [{ min: 0, max: 100, label: 'Responsive', color: '#000000', labelColor: '#000000', textColor: '#ffffff' }],
		redshiftQueryParams: {
			minPetitionDate: '2023-05-05',
			minRelevantPetitions: 0,
			minResponsivenessScore: 0,
			maxResponsivenessScore: 1,
			levels: ['federal', 'state', 'regional', 'city', 'local', 'county'],
		},
	})
	.toConfig({
		owner: 'squad-capybara',
		scope: 'corgi-front-end',
		context: 'Decision maker profile page responsiveness score',
		description:
			'Holds a config for the labels and color codes for the DM responsiveness score, as well as contact details.',
		template: {
			contact: {
				email: 'decisionmaker@change.org',
				phone: '+11 223 445 678',
			},
			tags: [
				{
					min: 0,
					max: 50,
					label: 'Responsive',
					color: '#eeaabb',
					labelColor: '#eeaabb',
					textColor: '#ffffff',
				},
				{
					min: 51,
					max: 100,
					label: 'Moderately responsive',
					color: '#eeaabb',
					labelColor: '#eeaabb',
					textColor: '#ffffff',
				},
			],
		},
	});
